/**
 * Juice Batch 1 — 結算畫面數字累加動畫的純函式核心。
 *
 * `tickValue(elapsedMs, durationMs, target)` 回傳「從 0 到 target」在 elapsedMs
 * 時間點應顯示的整數值,使用 ease-out (easeOutCubic) 讓數字一開始衝很快、
 * 接近尾聲慢下來,符合「爽感累加」手感。
 *
 * 純函式、無 Phaser / DOM 依賴 — 方便單元測試(0 / 中途 / 結束 / clamp)。
 *
 * 設計約束:
 *  - elapsedMs <= 0          → 0
 *  - elapsedMs >= durationMs → target(結束精確落在目標,不會少 1)
 *  - durationMs <= 0         → target(避免除以 0;視為瞬間完成)
 *  - 回傳一律 Math.round 後的整數(顯示用),且 clamp 在 [0, target]
 *    (target 為負時 clamp 在 [target, 0])。
 */

/** easeOutCubic — 0→1 緩出。t 已假定 clamp 在 [0,1]。 */
export function easeOutCubic(t: number): number {
  const inv = 1 - t;
  return 1 - inv * inv * inv;
}

/**
 * 計算累加動畫在 elapsedMs 時的顯示值(整數)。
 *
 * @param elapsedMs  自動畫開始經過的毫秒。
 * @param durationMs 動畫總長度(毫秒)。<=0 視為瞬間完成。
 * @param target     目標數值(動畫結束時的值)。可為 0 或負。
 */
export function tickValue(elapsedMs: number, durationMs: number, target: number): number {
  if (!Number.isFinite(target)) return 0;
  if (target === 0) return 0;
  // 瞬間完成 / 已結束 → 精確落在 target。
  if (durationMs <= 0 || elapsedMs >= durationMs) return Math.round(target);
  if (elapsedMs <= 0) return 0;

  const t = elapsedMs / durationMs; // (0,1)
  const eased = easeOutCubic(t);
  const raw = eased * target;
  const rounded = Math.round(raw);

  // Clamp 在 0 與 target 之間(支援 target 為負)。
  if (target >= 0) return Math.min(target, Math.max(0, rounded));
  return Math.max(target, Math.min(0, rounded));
}
